import ApplicationConfig from '../application-config'
import UTCDateTime from '../utc-datetime'
import connectHttpBase from './connect-http-base'
import UserInfo from '@/lib/user-info'

// Fire
const V1_IMPORT = '/v1/import'
const V1_FIELD_ITEMS = '/v1/field_items'
const V1_TASK_RECORD_ITEMS = '/v1/task_record_items'
const V1_REFLINE_ITEMS = '/v1/ref_line_items'

const V1_OBSERVATIONS = '/v1/observations'
const V1_PLANTINGS = '/v1/plantings'
const V1_FIELD_ELEVATION_MAP = '/v1/field_items'
const V1_OPERATIONS = '/v1/operations'

const V1_REALTIME = '/v1/realtime'

/**
 * ConnectDatastoreClient
 */
class ConnectDatastoreClient {
  /***************************
   * FieldItems
   ***************************/
  getFields2() {
    const endPoint = this.datastoreEndpoint(V1_FIELD_ITEMS)
    return connectHttpBase.getAuthenticatedHttpClient().get(endPoint)
  }

  getFields2ForPage(params) {
    const endPoint = this.datastoreEndpoint(V1_FIELD_ITEMS + '/page')
    return connectHttpBase.getAuthenticatedHttpClient().get(endPoint, {
      params: {
        page: params.pageOptions.page,
        pageSize: params.pageOptions.pageSize,
        sortKey: params.sortOptions.key,
        sortDirection: params.sortOptions.direction,
      },
    })
  }

  getCentroidOfUserFields() {
    const path = V1_FIELD_ITEMS + '/centroid'
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().get(endPoint)
  }

  uploadGeoFile(file, params) {
    const path = V1_IMPORT + '/geo_json'
    const endPoint = this.datastoreEndpoint(path)
    let form = new FormData()
    form.append('file', file)
    form.append('encoding', params.encoding)
    return connectHttpBase.getAuthenticatedHttpClient().post(endPoint, form, {
      headers: {
        'Content-Type': 'multipart-formdata',
      },
    })
  }

  updateFieldMetaData(updateRequest) {
    const field = updateRequest.field
    let path = V1_FIELD_ITEMS + '/' + field.id + '/meta'
    const endPoint = this.datastoreEndpoint(path)
    const request = {
      name: updateRequest.name,
      caption: updateRequest.caption,
      metaDataUpdatedAt: UTCDateTime.utcStringNow(),
    }
    return connectHttpBase.getAuthenticatedHttpClient().put(endPoint, request)
  }

  updateFieldPolygon(updateRequest) {
    const field = updateRequest.field
    let path = V1_FIELD_ITEMS + '/' + field.id + '/polygon'
    const endPoint = this.datastoreEndpoint(path)
    const request = {
      features: updateRequest.features,
    }
    return connectHttpBase.getAuthenticatedHttpClient().put(endPoint, request)
  }

  waitSyncAsUpdateFieldMetaData(request) {
    let path = V1_FIELD_ITEMS + '/sync/metadata'
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().post(endPoint, request)
  }

  deleteField(field) {
    const path = V1_FIELD_ITEMS + '/' + field.id
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().delete(endPoint)
  }

  countTasksForField(field) {
    const path = V1_FIELD_ITEMS + '/' + field.id + '/tasks/count'
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().get(endPoint)
  }

  /**
   * Import
   **/

  importFields(importFieldsRequest) {
    const path = V1_FIELD_ITEMS + ''
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().post(endPoint, importFieldsRequest)
  }

  /***************************
   * TaskRecordItem
   ***************************/
  getTasksWithFilter(filter, page) {
    const endPoint = this.datastoreEndpoint(V1_TASK_RECORD_ITEMS) + '?page=' + page.page + '&pageSize=' + page.pageSize
    return connectHttpBase.getAuthenticatedHttpClient().post(endPoint, filter)
  }

  getTaskInfo(task) {
    let path = V1_TASK_RECORD_ITEMS + '/info/' + task.id
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().get(endPoint)
  }

  updateTaskMetaData(updateRequest) {
    const task = updateRequest.task
    let path = V1_TASK_RECORD_ITEMS + '/' + task.id + '/meta'
    const endPoint = this.datastoreEndpoint(path)
    const request = {
      name: updateRequest.name,
      caption: updateRequest.caption,
      metaDatas: updateRequest.metaDatas,
      metaDataUpdatedAt: UTCDateTime.utcStringNow(),
    }
    return connectHttpBase.getAuthenticatedHttpClient().put(endPoint, request)
  }

  deleteTaskRecordItem(task) {
    const path = V1_TASK_RECORD_ITEMS + '/' + task.id
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().delete(endPoint)
  }

  getTaskRecordItem(params) {
    const endPoint = this.datastoreEndpoint(V1_TASK_RECORD_ITEMS + '/' + params.key)
    return connectHttpBase.getAuthenticatedHttpClient().get(endPoint)
  }

  waitSyncAsUpdateTaskMetaData(request) {
    let path = V1_TASK_RECORD_ITEMS + '/sync/metadata'
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().post(endPoint, request)
  }

  getTsvArchiveEndpoint(filterName, pageIndex, pageSize) {
    const path = V1_TASK_RECORD_ITEMS + '/archives/tsv/' + filterName + '?page=' + pageIndex + '&pageSize=' + pageSize
    return this.datastoreEndpoint(path)
  }

  saveTaskFilter(taskFilter) {
    const path = V1_TASK_RECORD_ITEMS + '/filters'
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().post(endPoint, taskFilter)
  }

  /***************************
   * RefLineItems
   ***************************/
  listRefLines(page) {
    const path = V1_REFLINE_ITEMS
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().get(endPoint, {
      params: {
        page: page.page,
        pageSize: page.pageSize,
      },
    })
  }

  updateRefLineMetaData(updateRequest) {
    const refLine = updateRequest.refLine
    let path = V1_REFLINE_ITEMS + '/' + refLine.id + '/meta'

    const endPoint = this.datastoreEndpoint(path)
    const request = {
      name: updateRequest.name,
      caption: updateRequest.caption,
      metaDataUpdatedAt: UTCDateTime.utcStringNow(),
    }
    return connectHttpBase.getAuthenticatedHttpClient().put(endPoint, request)
  }

  deleteRefLine(refLine) {
    const path = V1_REFLINE_ITEMS + '/' + refLine.id
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().delete(endPoint)
  }

  waitSyncAsUpdateRefLineMetaData(request) {
    let path = V1_REFLINE_ITEMS + '/sync/metadata'
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().post(endPoint, request)
  }

  importRefLines(request) {
    const path = V1_REFLINE_ITEMS
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().post(endPoint, request)
  }

  getRefLinesTsvArchiveEndpoint(pageIndex, pageSize) {
    const path = V1_REFLINE_ITEMS + '/archives/tsv?page=' + pageIndex + '&pageSize=' + pageSize
    return this.datastoreEndpoint(path)
  }

  getRefLineGeoJson(refLine) {
    const endPoint = this.datastoreEndpoint(V1_REFLINE_ITEMS + '/' + refLine.id + '/geo.json')
    return connectHttpBase.getAuthenticatedHttpClient().get(endPoint)
  }

  /***************************
   * Observations
   ***************************/
  // PfObservations
  listPfObservations() {
    const endPoint = this.datastoreEndpoint(V1_OBSERVATIONS + '/pf')
    return connectHttpBase.getAuthenticatedHttpClient().get(endPoint)
  }

  createPfObservation(request) {
    const endPoint = this.datastoreEndpoint(V1_OBSERVATIONS + '/pf')
    return connectHttpBase.getAuthenticatedHttpClient().post(endPoint, request.observation)
  }

  updatePfObservation(request) {
    const endPoint = this.datastoreEndpoint(V1_OBSERVATIONS + '/pf/' + request.id)
    return connectHttpBase.getAuthenticatedHttpClient().put(endPoint, request.observation)
  }

  deletePfObservation(request) {
    const endPoint = this.datastoreEndpoint(V1_OBSERVATIONS + '/pf/' + request.id)
    return connectHttpBase.getAuthenticatedHttpClient().delete(endPoint)
  }

  // AtObservations
  listAtObservations() {
    const endPoint = this.datastoreEndpoint(V1_OBSERVATIONS + '/at')
    return connectHttpBase.getAuthenticatedHttpClient().get(endPoint)
  }

  createAtObservation(request) {
    const endPoint = this.datastoreEndpoint(V1_OBSERVATIONS + '/at')
    return connectHttpBase.getAuthenticatedHttpClient().post(endPoint, request.observation)
  }

  updateAtObservation(request) {
    const endPoint = this.datastoreEndpoint(V1_OBSERVATIONS + '/at/' + request.id)
    return connectHttpBase.getAuthenticatedHttpClient().put(endPoint, request.observation)
  }

  deleteAtObservation(request) {
    const endPoint = this.datastoreEndpoint(V1_OBSERVATIONS + '/at/' + request.id)
    return connectHttpBase.getAuthenticatedHttpClient().delete(endPoint)
  }

  /***************************
   * Plantings
   ***************************/
  listPlantings() {
    const endPoint = this.datastoreEndpoint(V1_PLANTINGS)
    return connectHttpBase.getAuthenticatedHttpClient().get(endPoint)
  }

  createPlanting(request) {
    const path = V1_PLANTINGS
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().post(endPoint, request.planting)
  }

  updatePlanting(request) {
    const path = V1_PLANTINGS + '/' + request.id
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().put(endPoint, request.planting)
  }

  deletePlanting(request) {
    const path = V1_PLANTINGS + '/' + request.id
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getAuthenticatedHttpClient().delete(endPoint)
  }

  /***************************
   * 高低差マップ
   ***************************/
  getFieldElevationMap(field) {
    const endPoint = this.datastoreEndpoint(V1_FIELD_ELEVATION_MAP + '/' + field.id + '/elevation')
    return connectHttpBase.getAuthenticatedHttpClient().get(endPoint)
  }

  createFieldElevationMap(params) {
    let endPoint = this.datastoreEndpoint(V1_FIELD_ELEVATION_MAP + '/' + params.field.id + '/elevation')
    let request = {
      meshSize: params.meshSize,
      maxPointCount: params.maxPointCount,
      taskFetchLimit: params.taskFetchLimit,
      gnssStatusFilter: params.gnssStatusFilter,
    }
    if (params.zRange) {
      const zRange = params.zRange
      request['zMin'] = zRange.min
      request['zMax'] = zRange.max
    }
    // params.dateRangeの日付フォーマットは yyyy-MM-dd (時差は考慮しない)
    if (params.dateRange) {
      request['oldestTaskDate'] = params.dateRange.oldest
      request['newestTaskDate'] = params.dateRange.newest
    }

// TEMP     console.log(params)
    return connectHttpBase.getAuthenticatedHttpClient().post(endPoint, request)
  }

  /***************************
   * OperationType
   ***************************/
  listOperationAvailableTypes() {
    const path = V1_OPERATIONS + '/available/types'
    const endPoint = this.datastoreEndpoint(path)
    return connectHttpBase.getHttpClient().get(endPoint)
  }

  /***************************
   * RealTimeInfos
   ***************************/
  getRealtimeInfos(username, deviceId, trackKey) {
    const endPoint = this.datastoreEndpoint(V1_REALTIME + '/' + username + '/' + deviceId + '/' + trackKey)
    return connectHttpBase.getAuthenticatedHttpClient().get(endPoint)
  }

  datastoreEndpoint(path) {
    //console.log('call datastore server. @' + path)
    // 基本的にはありえないように制御したいが念の為、Freeの場合はdatastoreサーバを呼ばないようにする
    if (UserInfo.hasFreeFunctionality()) {
      throw new Error('FreeUser cannot access datastore server.')
    }
    return ApplicationConfig.getDatastoreServer() + path
  }
}

const connectDatastoreClient = new ConnectDatastoreClient()
export default connectDatastoreClient
